package co.maytes.standupbot.dashboard.features.roster

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Spinner
import co.maytes.standupbot.dashboard.components.widgets.TextInput
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.styles.FontWeight
import co.maytes.standupbot.dashboard.styles.IconSize
import co.maytes.standupbot.dashboard.styles.Motion
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Space
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.dashboard.styles.focusRingOutline
import co.maytes.standupbot.shared.design.StatusHue
import co.maytes.standupbot.shared.dto.slack.ChannelMemberDto
import co.maytes.standupbot.shared.dto.standup.LinkStateDto
import co.maytes.standupbot.shared.roster.LinearEmailMatcher
import co.maytes.standupbot.shared.roster.RosterSelection
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaLabel
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.role
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.selectors.focusVisible
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toModifier
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button as HtmlButton
import org.jetbrains.compose.web.dom.CheckboxInput
import org.jetbrains.compose.web.dom.Code
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

private const val LIST_MAX_HEIGHT_PX = 360
private const val AVATAR_PX = 32
private const val INVITE_COMMAND = "/invite @StandupBot"

@Composable
fun RosterPicker(
    state: RosterPickerState,
    channelName: String?,
    selected: Set<String>,
    onSelectionChange: (Set<String>) -> Unit,
    onRetry: () -> Unit,
    onCheckAgain: () -> Unit,
    modifier: Modifier = Modifier,
    linearPreview: LinearPreview = LinearPreview.Hidden,
) {
    val channelLabel = channelName?.let { "#$it" } ?: "this channel"
    Column(modifier = Modifier.fillMaxWidth().then(modifier).gap(Spacing.sm)) {
        when (state) {
            RosterPickerState.NoChannel -> QuietText("Pick a channel to see who is in it.")
            RosterPickerState.Loading -> LoadingRoster(channelLabel)
            is RosterPickerState.Ready ->
                ReadyRoster(
                    state = state,
                    channelLabel = channelLabel,
                    selected = selected,
                    onSelectionChange = onSelectionChange,
                    linearPreview = linearPreview,
                )
            RosterPickerState.BotNotInChannel -> BotNotInChannelNotice(channelLabel, onCheckAgain)
            RosterPickerState.SlackNotConnected ->
                Notice(tone = NoticeTone.Warning) {
                    Text("No Slack workspace is connected. ")
                    Link(path = "/app/settings/integrations", text = "Connect Slack first")
                }
            is RosterPickerState.SlackDidNotAnswer ->
                RetryNotice(headline = "Slack didn't answer. Try again.", detail = state.detail, onRetry = onRetry)
            is RosterPickerState.Failed ->
                RetryNotice(headline = "Couldn't load the people in $channelLabel.", detail = state.message, onRetry = onRetry)
        }
    }
}

@Composable
private fun LoadingRoster(channelLabel: String) {
    Row(
        modifier = Modifier.gap(Spacing.xs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Spinner(ariaLabel = "Loading people in $channelLabel")
        QuietText("Loading people in $channelLabel…")
    }
    SkeletonList(rows = 4, showAvatar = true)
}

@Composable
private fun BotNotInChannelNotice(channelLabel: String, onCheckAgain: () -> Unit) {
    Notice(tone = NoticeTone.Warning) {
        Text("StandupBot isn't in $channelLabel yet. In Slack, type ")
        InlineCode(INVITE_COMMAND)
        Text(", then press Check again.")
    }
    Row(modifier = Modifier.fillMaxWidth()) {
        ActionButton(label = "Check again", onClick = onCheckAgain)
    }
}

@Composable
private fun RetryNotice(headline: String, detail: String?, onRetry: () -> Unit) {
    Notice(tone = NoticeTone.Error) {
        Text(headline)
        if (!detail.isNullOrBlank()) {
            Span(attrs = Modifier.margin(left = Spacing.xs).color(Palette.textSecondary).toAttrs()) { Text(detail) }
        }
    }
    Row(modifier = Modifier.fillMaxWidth()) {
        ActionButton(label = "Retry", onClick = onRetry)
    }
}

@Composable
private fun ReadyRoster(
    state: RosterPickerState.Ready,
    channelLabel: String,
    selected: Set<String>,
    onSelectionChange: (Set<String>) -> Unit,
    linearPreview: LinearPreview,
) {
    var query by remember { mutableStateOf("") }
    val shown = remember(state.members, query) { RosterSelection.filter(state.members, query) }
    val counts = RosterSelection.counts(state.members, selected)
    val searching = query.isNotBlank()

    if (state.members.isEmpty()) {
        QuietText("Nobody else is in $channelLabel yet. You can add people later from the standup's Members tab.")
        return
    }

    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Div(
            attrs = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textPrimary)
                .toAttrs { attr("aria-live", "polite") },
        ) {
            Text("${counts.selected} of ${counts.total} selected")
        }
        Row(modifier = Modifier.gap(Spacing.xs), verticalAlignment = Alignment.CenterVertically) {
            TextAction(label = if (searching) "Select shown" else "Select all") {
                onSelectionChange(RosterSelection.selectAll(selected, shown))
            }
            Span(attrs = Modifier.color(Palette.textTertiary).toAttrs { attr("aria-hidden", "true") }) { Text("·") }
            TextAction(label = if (searching) "Clear shown" else "None") {
                onSelectionChange(RosterSelection.selectNone(selected, shown))
            }
        }
    }

    TextInput(
        value = query,
        onValueChange = { query = it },
        placeholder = "Search people…",
        ariaLabel = "Search people in $channelLabel",
    )

    val matches = (linearPreview as? LinearPreview.Ready)?.matches.orEmpty()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .role("group")
            .ariaLabel("People in $channelLabel")
            .padding(Spacing.xxs)
            .styleModifier {
                property("max-height", "${LIST_MAX_HEIGHT_PX}px")
                property("overflow-y", "auto")
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
                property("border-radius", "${Radius.md}")
            },
    ) {
        if (shown.isEmpty()) {
            QuietText("No one matches \"${query.trim()}\".", modifier = Modifier.padding(Spacing.sm))
        }
        shown.forEach { member ->
            RosterRow(
                member = member,
                checked = member.slackUserId in selected,
                linearChip = if (linearPreview is LinearPreview.Ready) matches[member.slackUserId]?.toLinkState() else null,
                onToggle = { onSelectionChange(RosterSelection.toggle(selected, member.slackUserId)) },
            )
        }
    }

    LinearSummary(linearPreview = linearPreview, members = state.members, selected = selected)

    if (state.truncated) {
        QuietText("Showing the first 1000 people. Add the rest later from the standup's Members tab.")
    }
}

@Composable
private fun LinearSummary(linearPreview: LinearPreview, members: List<ChannelMemberDto>, selected: Set<String>) {
    when (linearPreview) {
        LinearPreview.Hidden -> Unit
        LinearPreview.Loading -> QuietText("Matching people to Linear…")
        LinearPreview.NotConnected ->
            Notice(tone = NoticeTone.Info) {
                Text("Connect Linear to match people automatically. ")
                Link(path = "/app/settings/integrations", text = "Connect Linear")
            }
        LinearPreview.Unavailable ->
            QuietText("Couldn't load your Linear people, so matching is skipped. You can link people after the standup exists.")
        is LinearPreview.Ready -> {
            val counts = RosterSelection.counts(members, selected)
            val matched = LinearEmailMatcher.matchedCount(linearPreview.matches, selected)
            val unmatched = counts.selected - matched
            Div(
                attrs = Modifier
                    .fontSize(Typography.bodySmall.size)
                    .color(Palette.textSecondary)
                    .toAttrs { attr("aria-live", "polite") },
            ) {
                Text("$matched of ${counts.selected} matched to Linear by email.")
                if (unmatched > 0) {
                    Text(" $unmatched stay selected and show as \"no Linear activity\" until you link them.")
                }
            }
        }
    }
}

val RosterRowStyle = CssStyle {
    base {
        Modifier.styleModifier {
            property("transition", Motion.TRANSITION_HOVER)
        }
    }
    hover {
        Modifier.backgroundColor(Palette.surfaceHover)
    }
}

@Composable
private fun RosterRow(
    member: ChannelMemberDto,
    checked: Boolean,
    linearChip: LinkStateDto?,
    onToggle: () -> Unit,
) {
    val disabled = member.alreadyOnRoster
    Label(
        attrs = RosterRowStyle.toModifier()
            .asCheckRow()
            .fillMaxWidth()
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.sm)
            .cursor(if (disabled) Cursor.Default else Cursor.Pointer)
            .styleModifier {
                property("display", "flex")
                property("align-items", "center")
                property("gap", "${Spacing.sm}")
                property("box-sizing", "border-box")
                if (checked && !disabled) property("background-color", Palette.accentSubtle.cssRgb())
                if (disabled) property("opacity", "0.6")
            }
            .toAttrs(),
    ) {
        RowCheckControl(checked = checked || disabled, disabled = disabled, onToggle = onToggle)
        Avatar(
            name = member.displayName,
            src = member.avatarUrl,
            sizePx = AVATAR_PX,
            tone = if (member.isGuest) AvatarTone.Muted else AvatarTone.Accent,
        )
        Column(
            modifier = Modifier.styleModifier {
                property("flex", "1 1 auto")
                property("min-width", "0")
            },
        ) {
            Row(modifier = Modifier.gap(Spacing.xs), verticalAlignment = Alignment.CenterVertically) {
                SpanText(
                    text = member.displayName,
                    modifier = Modifier
                        .fontSize(Typography.body.size)
                        .fontWeight(500)
                        .color(Palette.textPrimary)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        },
                )
                if (member.isGuest) Badge(label = "guest", tone = BadgeTone.Neutral)
                if (member.alreadyOnRoster) Badge(label = "on roster", tone = BadgeTone.Neutral)
            }
            member.email?.let {
                SpanText(
                    text = it,
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary)
                        .styleModifier {
                            property("overflow", "hidden")
                            property("text-overflow", "ellipsis")
                            property("white-space", "nowrap")
                        },
                )
            }
        }
        linearChip?.let { LinkStateChip(provider = "Linear", state = it) }
    }
}

private enum class NoticeTone { Warning, Error, Info }

@Composable
private fun Notice(tone: NoticeTone, content: @Composable () -> Unit) {
    val (background, border) =
        when (tone) {
            NoticeTone.Warning -> Palette.status(StatusHue.Warning).let { it.subtle.cssRgb() to it.border.cssRgb() }
            NoticeTone.Error -> Palette.status(StatusHue.Danger).let { it.subtle.cssRgb() to it.border.cssRgb() }
            NoticeTone.Info -> Palette.accentSubtle.cssRgb() to Palette.border.cssRgb()
        }
    Div(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.sm, leftRight = Spacing.md)
            .borderRadius(Radius.md)
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textPrimary)
            .styleModifier {
                property("background", background)
                property("border", "1px solid $border")
                property("line-height", "1.6")
                property("box-sizing", "border-box")
            }
            .toAttrs { attr("role", if (tone == NoticeTone.Info) "note" else "alert") },
    ) {
        content()
    }
}

@Composable
private fun InlineCode(text: String) {
    Code(
        attrs = Modifier
            .padding(topBottom = Space.s0_5, leftRight = Spacing.xxs)
            .borderRadius(Radius.sm)
            .backgroundColor(Palette.surfaceSubtle)
            .fontSize(Typography.caption.size)
            .styleModifier {
                property("font-family", Typography.FONT_MONO)
            }
            .toAttrs(),
    ) {
        Text(text)
    }
}

@Composable
private fun QuietText(text: String, modifier: Modifier = Modifier) {
    SpanText(
        text = text,
        modifier = Modifier
            .fontSize(Typography.bodySmall.size)
            .color(Palette.textSecondary)
            .styleModifier { property("line-height", "1.6") }
            .then(modifier),
    )
}

@Composable
fun ActionButton(label: String, onClick: () -> Unit) {
    Button(label = label, variant = ButtonVariant.Secondary, onClick = onClick)
}

val RosterTextActionStyle = CssStyle {
    base {
        Modifier
            .color(Palette.accent)
            .fontSize(Typography.bodySmall.size)
            .fontWeight(FontWeight.medium)
            .padding(topBottom = Space.s1, leftRight = Space.s2)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", "transparent")
                property("border", "none")
                property("border-radius", "${Radius.sm}")
                property("font-family", Typography.FONT_SANS)
            }
    }
    hover {
        Modifier.styleModifier { property("text-decoration", "underline") }
    }
    focusVisible {
        Modifier.focusRingOutline()
    }
}

@Composable
fun TextAction(label: String, enabled: Boolean = true, onClick: () -> Unit) {
    HtmlButton(
        attrs = RosterTextActionStyle.toModifier().toAttrs {
            attr("type", "button")
            if (!enabled) disabled()
            onClick { onClick() }
        },
    ) {
        Text(label)
    }
}
